// components/payments/TransactionsTable.tsx
import React from "react";
import { ExternalLink, CheckCircle, Clock, XCircle } from "lucide-react";
import { Link } from "react-router-dom";
import EmptyState from "./EmptyState";
import { Transaction } from "../../lib/types/payments";

interface TransactionsTableProps {
  transactions: Transaction[];
  searchQuery: string;
  statusFilter: string;
}

const TransactionsTable: React.FC<TransactionsTableProps> = ({
  transactions,
  searchQuery,
  statusFilter,
}) => {
  // Filter transactions based on search and status
  const filteredTransactions = transactions.filter((transaction) => {
    const matchesSearch =
      searchQuery === "" ||
      transaction.transactionId
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      transaction.description
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      transaction.taskId?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || transaction.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format amount helper
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  // Status badge component
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100">
            <CheckCircle className="w-3.5 h-3.5" />
            Completed
          </span>
        );
      case "pending":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-100">
            <Clock className="w-3.5 h-3.5" />
            Pending
          </span>
        );
      case "failed":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-100">
            <XCircle className="w-3.5 h-3.5" />
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-700 border border-gray-100">
            {status}
          </span>
        );
    }
  };

  // Get type badge
  const getTypeBadge = (type: string) => {
    const typeColors: Record<string, string> = {
      task_earning: "bg-blue-50 text-blue-700 border-blue-100",
      bonus: "bg-purple-50 text-purple-700 border-purple-100",
      referral: "bg-indigo-50 text-indigo-700 border-indigo-100",
      adjustment: "bg-orange-50 text-orange-700 border-orange-100",
    };

    const typeLabels: Record<string, string> = {
      task_earning: "Task Earning",
      bonus: "Bonus",
      referral: "Referral",
      adjustment: "Adjustment",
    };

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${typeColors[type] || "bg-gray-50 text-gray-700 border-gray-100"}`}
      >
        {typeLabels[type] || type}
      </span>
    );
  };
  const hasFilters = searchQuery !== "" || statusFilter !== "all";

  if (filteredTransactions.length === 0) {
    return <EmptyState type="transactions" hasFilters={hasFilters} />;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Transaction ID
            </th>
            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Date
            </th>
            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Description
            </th>
            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Task ID
            </th>
            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Amount
            </th>
            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {filteredTransactions.map((transaction) => (
            <tr
              key={transaction._id}
              className="hover:bg-gray-50/50 transition-colors"
            >
              <td className="py-4 px-4">
                <span className="text-sm font-mono text-gray-600">
                  {transaction.transactionId}
                </span>
              </td>
              <td className="py-4 px-4">
                <span className="text-sm text-gray-600">
                  {formatDate(transaction.createdAt)}
                </span>
              </td>
              <td className="py-4 px-4">
                <span className="text-sm text-gray-900">
                  {transaction.description}
                </span>
              </td>
              <td className="py-4 px-4">{getTypeBadge(transaction.type)}</td>
              <td className="py-4 px-4">
                <span className="text-sm font-mono text-gray-500">
                  {transaction.taskId || "—"}
                </span>
              </td>
              <td className="py-4 px-4 text-right">
                <span
                  className={`text-sm font-semibold ${
                    transaction.amount >= 0 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {transaction.amount >= 0 ? "+" : ""}
                  {formatAmount(transaction.amount)}
                </span>
              </td>
              <td className="py-4 px-4">
                {getStatusBadge(transaction.status)}
              </td>
              <td className="py-4 px-4 text-right">
                {transaction.taskId && (
                  <Link
                    to={`/tasks/${transaction.taskId}`}
                    className="inline-flex items-center gap-1 text-sm text-[#8B52FF] hover:text-[#7040E0] font-medium transition-colors"
                  >
                    View Task
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Link>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TransactionsTable;
